import { Layout } from "@/components/layout/layout";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { useState } from "react";
import { useRequestPlayerPasswordSetup, ApiError } from "@workspace/api-client-react";
import { usePlayerAuthActions } from "@/hooks/useCurrentPlayer";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

type ErrorBody = { error?: string; code?: string };

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { login } = usePlayerAuthActions();
  const requestSetup = useRequestPlayerPasswordSetup();

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    login.mutate(
      { data: { email: email.trim(), password } },
      {
        onSuccess: (player) => {
          toast({
            title: "Welcome back",
            description: `Signed in as ${player.name}`,
          });
          setLocation("/me");
        },
        onError: (err: unknown) => {
          const body =
            err instanceof ApiError && err.data && typeof err.data === "object"
              ? (err.data as ErrorBody)
              : null;
          if (body?.code === "no_password_set") {
            toast({
              title: "Set your password",
              description:
                "This account has no password yet. Use 'Email me a setup link' below to finish setup.",
              variant: "destructive",
            });
          } else {
            toast({
              title: "Sign in failed",
              description: body?.error ?? "Invalid email or password.",
              variant: "destructive",
            });
          }
        },
      },
    );
  };

  const handleSendSetupLink = () => {
    if (!email) {
      toast({
        title: "Enter your email",
        description: "Type your email above first, then we'll send you a link.",
      });
      return;
    }
    requestSetup.mutate(
      { data: { email: email.trim() } },
      {
        onSuccess: () => {
          toast({
            title: "Check your email",
            description:
              "If an account exists for that email, we've sent a link to set a password.",
          });
        },
        onError: () => {
          toast({
            title: "Couldn't send link",
            description: "Please try again in a moment.",
            variant: "destructive",
          });
        },
      },
    );
  };

  return (
    <Layout>
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-background relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-secondary/10 via-background to-background" />

        <Card className="w-full max-w-md relative z-10 border-border/50 shadow-2xl shadow-primary/5">
          <CardHeader className="space-y-1 text-center pb-8 pt-8">
            <CardTitle className="text-3xl font-bold tracking-tight">Sign In</CardTitle>
            <CardDescription className="text-base text-muted-foreground pt-2">
              Enter your email and password to access your player profile.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground font-medium">
                  Email address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="h-12 bg-muted/50 focus:bg-background transition-colors"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-foreground font-medium">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="h-12 bg-muted/50 focus:bg-background transition-colors"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold"
                disabled={login.isPending || !email || !password}
              >
                {login.isPending ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}
                {login.isPending ? "Signing In..." : "Sign In"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full text-sm text-muted-foreground hover:text-primary"
                disabled={requestSetup.isPending}
                onClick={handleSendSetupLink}
              >
                {requestSetup.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Email me a setup / reset link
              </Button>
            </form>
          </CardContent>
          <CardFooter className="flex flex-col items-center justify-center border-t border-border/50 p-6 bg-muted/10">
            <p className="text-sm text-muted-foreground">
              Don't have a profile yet?{" "}
              <Link href="/signup" className="text-primary font-semibold hover:underline">
                Join the Circuit
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </Layout>
  );
}
